import {
    ClauseGuidanceRefreshUnconfirmedError,
    ClauseGuidanceRefreshDivergentError,
    ClauseGuidanceWriteConflictError,
    ClauseGuidanceWriteDecision,
    ClauseGuidanceWriteError,
    decideClauseGuidanceSave,
    IOrganizationClauseGuidance,
    IOrganizationClauseGuidanceUpdate,
    useIsClauseGuidancePending,
    useSaveClauseGuidance,
} from "@/api";
import { IClauseList } from "@/api/clauses/clauses.types";
import { Button } from "@/components/shared/buttons/Button";
import { RestartAlt, Save } from "@mui/icons-material";
import { Stack } from "@mui/material";
import { FormContainer, FormTextFieldElement } from "@rhf-kit/mui";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";

interface IProps {
    /**
     * The clause to display guidance for
     */
    clause: IClauseList;
    /**
     * The organization's own guidance row when editing began
     */
    own: IOrganizationClauseGuidance | null;
    /**
     * The text the editor started from
     */
    initialValue: string;
    /**
     * Callback to close the editor
     */
    onClose: () => void;
    /**
     * Callback when a write landed but the guidance could not be reloaded
     */
    onUnconfirmed: (write: ClauseGuidanceWriteDecision) => void;
    /**
     * Callback with the organization's own guidance row as the server now has it
     */
    onOwnChanged: (own: IOrganizationClauseGuidance | null) => void;
    /**
     * Callback that reloads the guidance after a write found the organization's own guidance changed
     */
    onWriteConflict: () => Promise<void>;
}

const writeErrorMessage = (
    write: ClauseGuidanceWriteDecision,
    error: unknown
) => {
    const outcome =
        write.kind === "delete"
            ? "Custom guidance was not removed."
            : "Custom guidance was not saved.";
    const reason =
        error instanceof ClauseGuidanceWriteError && error.detail
            ? error.detail
            : "Try again.";
    return `${outcome} ${reason}`;
};

/**
 * Form for editing the guidance for a clause
 */
export const CustomGuidanceForm = ({
    clause,
    own,
    initialValue,
    onClose,
    onUnconfirmed,
    onOwnChanged,
    onWriteConflict,
}: IProps) => {
    const { mutateAsync: saveGuidance, isPending } = useSaveClauseGuidance(
        clause.id
    );
    const isGuidancePending = useIsClauseGuidancePending(clause.id);
    const [saveError, setSaveError] = useState<string | null>(null);
    const editorRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (saveError && !isPending) {
            editorRef.current?.focus();
        }
    }, [saveError, isPending]);

    const handleSubmit = async ({
        guidance,
    }: IOrganizationClauseGuidanceUpdate) => {
        const decision = decideClauseGuidanceSave({
            own,
            initialValue,
            value: guidance,
        });
        if (decision.kind === "none") {
            onClose();
            return;
        }

        setSaveError(null);
        try {
            await saveGuidance(decision);
            onClose();
        } catch (error) {
            if (error instanceof ClauseGuidanceRefreshUnconfirmedError) {
                onUnconfirmed(error.confirmedWrite);
                return;
            }
            if (error instanceof ClauseGuidanceRefreshDivergentError) {
                onOwnChanged(error.observedGuidance.own);
                const message =
                    "Guidance changed after your save. Your text is still in the editor. Reload before saving again.";
                enqueueSnackbar(message, { variant: "error" });
                setSaveError(message);
                return;
            }
            if (error instanceof ClauseGuidanceWriteConflictError) {
                await onWriteConflict();
            }
            const message = writeErrorMessage(decision, error);
            enqueueSnackbar(message, { variant: "error" });
            setSaveError(message);
        }
    };

    return (
        <FormContainer
            onSuccess={handleSubmit}
            defaultValues={{ guidance: initialValue }}
        >
            <Stack gap={1.5}>
                <FormTextFieldElement
                    name="guidance"
                    label="Edit Guidance"
                    inputRef={editorRef}
                    autoFocus
                    fullWidth
                    multiline
                    disabled={isPending}
                    rows={6}
                    helperText={saveError}
                    slotProps={{
                        formHelperText: { role: "alert", error: true },
                    }}
                />

                <Stack direction="row" justifyContent="flex-end" gap={1.5}>
                    <Button
                        variant="outlined"
                        type="button"
                        size="small"
                        startIcon={<RestartAlt />}
                        onClick={onClose}
                        disabled={isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        size="small"
                        startIcon={<Save />}
                        loading={isPending}
                        disabled={isGuidancePending}
                    >
                        Save
                    </Button>
                </Stack>
            </Stack>
        </FormContainer>
    );
};
